/**
  * vue-class-component v7.4.1
  * (c) 2015-present Evan You
  * @license MIT
  */
import Vue from 'vue';

function createDecorator(factory) {
  return (target, key, index) => {
    const Ctor = typeof target === 'function' ? target : target.constructor;
    if (!Ctor.__decorators__) {
      Ctor.__decorators__ = [];
    }
    if (typeof index !== 'number') {
      index = undefined;
    }
    Ctor.__decorators__.push(options => factory(options, key, index));
  };
}
function mixins(...Ctors) {
  // @ts-ignore
  return Vue.extend({
    mixins: Ctors
  });
}
function warn(message) {
  if (typeof console !== 'undefined') {
    console.warn('[vue-class-component] ' + message);
  }
}

function collectDataFromConstructor(vm, Component) {
  // override _init to prevent to init as Vue instance
  const originalInit = Component.prototype._init;
  Component.prototype._init = function () {
    // proxy to actual vm
    const keys = Object.getOwnPropertyNames(vm);
    // 2.2.0 compat (props are no longer exposed as self properties)
    if (vm.$options.props) {
      for (const key in vm.$options.props) {
        if (!vm.hasOwnProperty(key)) {
          keys.push(key);
        }
      }
    }
    for (const key of keys) {
      Object.defineProperty(this, key, {
        get: () => vm[key],
        set: value => {
          vm[key] = value;
        },
        configurable: true
      });
    }
  };
  // should be acquired class property values
  const data = new Component();
  // restore original _init to avoid memory leak (#209)
  Component.prototype._init = originalInit;
  // create plain data object
  const plainData = {};
  for (const key of Object.keys(data)) {
    if (data[key] !== undefined) {
      plainData[key] = data[key];
    }
  }
  if (process.env.NODE_ENV !== 'production') {
    // @ts-ignore
    if (!(Component.prototype instanceof Vue) && Object.keys(plainData).length > 0) {
      warn('Component class must inherit Vue or its descendant class ' + 'when class property is used.');
    }
  }
  return plainData;
}

const $internalHooks = new Set(['data', 'beforeCreate', 'created', 'beforeMount', 'mounted', 'beforeDestroy', 'destroyed', 'beforeUpdate', 'updated', 'activated', 'deactivated', 'render', 'errorCaptured', 'serverPrefetch' // 2.6
]);

function componentFactory(Component, options = {}) {
  options.name = options.name || Component._componentTag || Component.name;
  // prototype props.
  const proto = Component.prototype;
  for (const key of Object.getOwnPropertyNames(proto)) {
    if (key === 'constructor') {
      continue;
    }
    // hooks
    if ($internalHooks.has(key)) {
      options[key] = proto[key];
      continue;
    }
    const descriptor = Object.getOwnPropertyDescriptor(proto, key);
    if (descriptor.value !== void 0) {
      // methods
      if (typeof descriptor.value === 'function') {
        (options.methods || (options.methods = {}))[key] = descriptor.value;
      } else {
        // typescript decorated data
        (options.mixins || (options.mixins = [])).push({
          data() {
            return {
              [key]: descriptor.value
            };
          }
        });
      }
    } else if (descriptor.get || descriptor.set) {
      // computed properties
      (options.computed || (options.computed = {}))[key] = {
        get: descriptor.get,
        set: descriptor.set
      };
    }
  }
  (options.mixins || (options.mixins = [])).push({
    data() {
      return collectDataFromConstructor(this, Component);
    }
  });
  // decorate options
  const decorators = Component.__decorators__;
  if (decorators) {
    for (const fn of decorators) {
      fn(options);
    }
    delete Component.__decorators__;
  }
  // find super
  const superProto = Object.getPrototypeOf(Component.prototype);
  // @ts-ignore
  const Super = superProto instanceof Vue ? superProto.constructor : Vue;
  const Extended = Super.extend(options);
  forwardStaticMembers(Extended, Component);
  return Extended;
}
const reservedPropertyNames = [
// Unique id
'cid',
// Super Vue constructor
'super',
// Component options that will be used by the component
'options', 'superOptions', 'extendOptions', 'sealedOptions',
// Private assets
'component', 'directive', 'filter'];
const shouldIgnore = {
  prototype: true,
  arguments: true,
  callee: true,
  caller: true
};
function forwardStaticMembers(Extended, Original) {
  // We have to use getOwnPropertyNames since Babel registers methods as non-enumerable
  for (const key of Object.getOwnPropertyNames(Original)) {
    // Skip the properties that should not be overwritten
    if (shouldIgnore[key]) {
      continue;
    }
    // Some browsers does not allow reconfigure built-in properties
    const extendedDescriptor = Object.getOwnPropertyDescriptor(Extended, key);
    if (extendedDescriptor && !extendedDescriptor.configurable) {
      continue;
    }
    const descriptor = Object.getOwnPropertyDescriptor(Original, key);
    // Warn if the users manually declare reserved properties
    if (process.env.NODE_ENV !== 'production' && reservedPropertyNames.indexOf(key) >= 0) {
      warn(`Static property name '${key}' declared on class '${Original.name}' ` + 'conflicts with reserved property name of Vue internal. ' + 'It may cause unexpected behavior of the component. Consider renaming the property.');
    }
    Object.defineProperty(Extended, key, descriptor);
  }
}

function Component(options) {
  if (typeof options === 'function') {
    return componentFactory(options);
  }
  return function (Component) {
    return componentFactory(Component, options);
  };
}
Component.registerHooks = function registerHooks(keys) {
  for (const k of keys) {
    $internalHooks.add(k);
  }
};

export { Component, createDecorator, Component as default, mixins };
